import { focusTarget } from "@/ui/a11y/focusTarget";
import { SPECIES_CHECK_HINT } from "@/ui/intake/intakeCopy";

export type IntakeFocusField = "species" | "weight" | "name" | "foundOn" | "notes";
export type IntakeFocusTarget =
  | "careSite"
  | "weight"
  | "name"
  | "sex"
  | "foundOn"
  | "foundPlace"
  | "save";

/**
 * Enter-Kette der Einzelaufnahme, an der Sichtreihenfolge.
 * Nach dem Namen kommt das Geschlecht, nicht das Funddatum.
 * Im Funddatum ist der Fundort das nächste Feld.
 * Auf Touch gibt es keinen Autofokus aufs Gewicht.
 */
export function intakeFocusAfter(
  field: IntakeFocusField,
  careSiteSet: boolean,
): IntakeFocusTarget {
  if (field === "species") return careSiteSet ? "weight" : "careSite";
  if (field === "weight") return "name";
  if (field === "name") return "sex";
  if (field === "foundOn") return "foundPlace";
  return "save";
}

export function shouldAutofocusIntakeWeight(coarse: boolean): boolean {
  return !coarse;
}

type SpeciesFocusTarget = {
  focus?: (options?: { preventScroll?: boolean; focusVisible?: boolean }) => void;
  scrollIntoView?: (options?: { block?: "center" }) => void;
  measureLayout?: (
    relative: number,
    onSuccess: (x: number, y: number, width: number, height: number) => void,
    onFail: () => void,
  ) => void;
};

type SpeciesScrollParent = {
  scrollTo?: (options: { y: number; animated?: boolean }) => void;
  measure?: (
    callback: (x: number, y: number, width: number, height: number) => void,
  ) => void;
};

/**
 * Chip, sonst das Freitextfeld. preventScroll, danach das Art-Feld in die Mitte.
 * Ohne scrollIntoView misst das Feld. Die Liste liefert per measure ihre Höhe,
 * damit Phone nicht an den oberen Rand springt. Fehlt measure, bleibt ein
 * Abstand von 8 px.
 * Beim ersten Öffnen bleibt der Fokus, wo er ist.
 */
/** „chip“, wenn der Fokus auf dem Chip landet, sonst das Feld oder nichts. */
export function focusSpeciesControl(
  chip: SpeciesFocusTarget | null | undefined,
  input: SpeciesFocusTarget | null | undefined,
  field?: SpeciesFocusTarget | null,
  scrollParent?: SpeciesScrollParent | null,
  relativeHandle?: number | null,
): "chip" | "input" | null {
  const useChip = chip != null && typeof chip.focus === "function";
  const target = useChip ? chip : input != null && typeof input.focus === "function" ? input : null;
  const landed: "chip" | "input" | null = useChip ? "chip" : target ? "input" : null;
  // Fehlerfokus: Ring erzwingen, soweit der Browser die Option kennt.
  if (target) focusTarget(target, true);
  if (typeof field?.scrollIntoView === "function") {
    field.scrollIntoView({ block: "center" });
    return landed;
  }
  if (typeof target?.scrollIntoView === "function") {
    target.scrollIntoView({ block: "center" });
    return landed;
  }
  if (
    !field ||
    typeof field.measureLayout !== "function" ||
    typeof scrollParent?.scrollTo !== "function" ||
    typeof relativeHandle !== "number"
  ) {
    return landed;
  }
  const scrollTo = scrollParent.scrollTo.bind(scrollParent);
  const place = (viewportHeight: number | null) => {
    field.measureLayout(
      relativeHandle,
      (_x, y, _width, height) => {
        const inset =
          viewportHeight == null ? 8 : Math.max(0, (viewportHeight - height) / 2);
        scrollTo({ y: Math.max(0, y - inset), animated: true });
      },
      () => {
        // Die Liste hat das Feld nicht gemessen. Der Fokus auf dem Chip bleibt.
      },
    );
  };
  if (typeof scrollParent.measure === "function") {
    scrollParent.measure((_x, _y, _width, height) => place(height));
    return landed;
  }
  place(null);
  return landed;
}

/** Fußzeile zur Art ist sichtbar, wird aber nicht angesagt. Das Feld bleibt der Alert. */
export function intakeStatusLiveProps(
  text: string,
  tone: "muted" | "danger",
): {
  accessibilityRole?: "alert";
  accessibilityLiveRegion?: "polite" | "assertive";
} {
  if (text === SPECIES_CHECK_HINT) return {};
  if (tone === "danger") {
    return { accessibilityRole: "alert", accessibilityLiveRegion: "assertive" };
  }
  return { accessibilityLiveRegion: "polite" };
}
